// Gift with purchase: client-side qualification check after every cart change.
//
// The prompt itself is rendered server-side (snippets/cart-gwp-prompt.liquid)
// and refreshed by the section morph that follows every cart change. This
// script covers the two things Liquid cannot do:
// 1. Remove the gift line (property _gwp) when no qualifying product is left.
// 2. Cap the gift line at one unit.
// It reads the qualifying product ids exported to the page as JSON and the
// cart JSON, then changes the cart through the same change endpoint the
// drawer uses and dispatches the theme's cart:update event with the fresh
// section HTML so every cart-items-component morphs consistently.
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

(() => {
  if (window.__cartGwp) return;
  window.__cartGwp = true;

  let inFlight = false;

  const readConfig = () => {
    const el = document.querySelector('[data-cart-gwp-config]');
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent || '{}');
      return {
        enabled: cfg.enabled !== false,
        giftProductId: Number(cfg.giftProductId),
        giftVariantId: Number(cfg.giftVariantId),
        qualifyingIds: new Set((cfg.qualifyingIds || []).map(Number)),
      };
    } catch (e) {
      return null;
    }
  };

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  // Only lines added through the prompt carry _gwp. A full-price copy of the
  // gift product added from its own page is never touched.
  const isGiftLine = (item) => Boolean(item.properties && item.properties._gwp);

  const change = async (body) => {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(body) }));
    return response.json();
  };

  const check = async () => {
    if (inFlight) return;
    const cfg = readConfig();
    if (!cfg) return;

    inFlight = true;
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const items = cart.items || [];
      const giftLine = items.find(isGiftLine);
      if (!giftLine) return;

      const qualifies =
        cfg.enabled && items.some((item) => !isGiftLine(item) && cfg.qualifyingIds.has(item.product_id));
      const sections = sectionIds().join(',');
      let result = null;

      if (!qualifies) {
        result = await change({ id: giftLine.key, quantity: 0, sections, sections_url: window.location.pathname });
      } else if (giftLine.quantity > 1) {
        result = await change({ id: giftLine.key, quantity: 1, sections, sections_url: window.location.pathname });
      }

      if (result && !result.errors) {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'cart-gwp', {
            source: 'cart-gwp',
            itemCount: result.item_count,
            sections: result.sections,
          })
        );
      }
    } catch (error) {
      console.error('[cart-gwp]', error);
    } finally {
      inFlight = false;
    }
  };

  // "Adding..." feedback on the prompt button, mirroring cart-upsells.js. The
  // morph that follows the add removes the prompt, so no reset is needed
  // unless the add fails.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('.cart-gwp__form')) return;
      const button = form.querySelector('.cart-gwp__add');
      if (!(button instanceof HTMLElement)) return;
      button.dataset.idleText = button.textContent || '';
      button.setAttribute('disabled', '');
      button.textContent = 'Adding...';
    },
    true
  );
  document.addEventListener(
    ThemeEvents.cartError,
    () => {
      document.querySelectorAll('.cart-gwp__add[disabled]').forEach((button) => {
        if (!(button instanceof HTMLElement) || !button.dataset.idleText) return;
        button.textContent = button.dataset.idleText;
        button.removeAttribute('disabled');
      });
    },
    true
  );

  // Run after the theme's own handlers so the morph has already happened.
  document.addEventListener(ThemeEvents.cartUpdate, () => setTimeout(check, 0));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', check, { once: true });
  } else {
    check();
  }
})();
